import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Pencil, Clock, Flag, BookOpen, Layers, CheckCircle2, UserPlus } from "lucide-react";

const DISC = {
  drone: { label: "Drone", cls: "bg-lime-100 text-lime-700 border-lime-200" },
  helicopter: { label: "Helicopter", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  aeroplane: { label: "Fixed Wing", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  other: { label: "Other", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const LEVEL_CLS = {
  RPL: "bg-lime-100 text-lime-700 border-lime-200",
  PPL: "bg-sky-100 text-sky-700 border-sky-200",
  CPL: "bg-indigo-100 text-indigo-700 border-indigo-200",
  Endorsement: "bg-purple-100 text-purple-700 border-purple-200",
};

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="bg-white/10 rounded-xl px-4 py-3">
      <div className="flex items-center gap-1.5 text-lime-400"><Icon className="h-4 w-4" /><span className="font-heading font-bold text-xl text-white">{value}</span></div>
      <p className="text-xs text-slate-300 mt-0.5">{label}</p>
    </div>
  );
}

export default function ProgramOverview({ program, onBack, onEdit, onAssign }) {
  const d = DISC[program.discipline] || DISC.other;
  const lessons = program.lessons || [];
  const phases = (program.phases || []).slice().sort((a, b) => a.seq - b.seq);
  const theoryUnits = (program.theory_units || []).slice().sort((a, b) => a.seq - b.seq);
  const phaseName = (pid) => phases.find((p) => p.id === pid)?.name;
  const lessonsByPhase = (pid) => lessons.filter((l) => l.phase_id === pid).sort((a, b) => a.seq - b.seq);
  const totalDual = lessons.reduce((s, l) => s + (Number(l.dual_time) || 0), 0);
  const totalSolo = lessons.reduce((s, l) => s + (Number(l.solo_time) || 0), 0);
  const milestones = lessons.filter((l) => l.milestone);

  return (
    <div className="space-y-6 fade-up" data-testid="program-overview">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="overview-back-btn"><ArrowLeft className="h-4 w-4" /> Back to syllabi</button>

      <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <Badge className={`border ${d.cls} mb-2`}>{d.label} Syllabus</Badge>
            <h1 className="text-2xl sm:text-3xl font-bold font-heading">{program.name}</h1>
            {program.regulatory_ref && <p className="text-slate-300 text-sm mt-1">Reference: {program.regulatory_ref}</p>}
          </div>
          <div className="flex gap-2 shrink-0">
            {onAssign && <Button data-testid="overview-assign-btn" onClick={onAssign} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold"><UserPlus className="h-4 w-4 mr-1.5" /> Enrol trainee</Button>}
            <Button data-testid="overview-edit-btn" variant="outline" onClick={onEdit} className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
          </div>
        </div>

        {program.description && <p className="text-slate-200 mt-5 max-w-3xl leading-relaxed">{program.description}</p>}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
          <Stat icon={BookOpen} value={lessons.length} label="Lessons" />
          <Stat icon={Layers} value={phases.length} label="Phases" />
          <Stat icon={BookOpen} value={theoryUnits.length} label="Theory units" />
          <Stat icon={Clock} value={`${Math.round((totalDual + totalSolo) * 10) / 10}h`} label="Est. total time" />
          <Stat icon={Flag} value={milestones.length} label="Milestones" />
        </div>
      </div>

      {theoryUnits.length > 0 && (
        <div data-testid="overview-theory">
          <h2 className="font-heading font-semibold text-slate-900 mb-1 flex items-center gap-2"><BookOpen className="h-5 w-5 text-lime-500" /> Theory course</h2>
          <p className="text-sm text-slate-500 mb-4">Ground-theory units — can be completed in one block or staged with the flight phases below.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {theoryUnits.map((u) => (
              <div key={u.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`overview-theory-${u.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-slate-900">{u.title}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {u.level && <Badge className={`border ${LEVEL_CLS[u.level] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{u.level}</Badge>}
                    {u.prerequisite && <Badge className="bg-orange-100 text-orange-700 border-orange-200 gap-1"><Flag className="h-3 w-3" /> Prereq</Badge>}
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">Pass mark {u.exam_pass_mark}%{u.phase_id && phaseName(u.phase_id) ? ` · staged with "${phaseName(u.phase_id)}"` : ""}</p>
                {u.topics?.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {u.topics.map((t) => <li key={t} className="flex items-start gap-1.5 text-sm text-slate-600"><CheckCircle2 className="h-3.5 w-3.5 text-slate-300 mt-0.5 shrink-0" /> {t}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-heading font-semibold text-slate-900 mb-1">Course outline</h2>
        <p className="text-sm text-slate-500 mb-4">Every lesson in this syllabus, grouped by phase.</p>
        <div className="space-y-6">
          {phases.map((ph, pi) => (
            <div key={ph.id} data-testid={`overview-phase-${ph.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="h-6 w-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">{pi + 1}</span>
                <h3 className="font-heading font-semibold text-slate-800">{ph.name}</h3>
                <span className="text-xs text-slate-400">· {lessonsByPhase(ph.id).length} lessons</span>
              </div>
              <div className="space-y-2 pl-8">
                {lessonsByPhase(ph.id).map((l, li) => (
                  <div key={l.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`overview-lesson-${l.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400 font-medium">{pi + 1}.{li + 1}</span>
                          <span className="font-medium text-slate-900">{l.title}</span>
                          {l.milestone && <Badge className="bg-orange-100 text-orange-700 border-orange-200 gap-1"><Flag className="h-3 w-3" /> Milestone</Badge>}
                        </div>
                        {l.criteria?.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {l.criteria.map((c) => (
                              <li key={c} className="flex items-start gap-1.5 text-sm text-slate-600"><CheckCircle2 className="h-3.5 w-3.5 text-slate-300 mt-0.5 shrink-0" /> {c}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {(l.dual_time > 0 || l.solo_time > 0) && (
                        <span className="text-xs text-slate-400 whitespace-nowrap shrink-0">
                          {l.dual_time > 0 && `${l.dual_time}h dual`}{l.dual_time > 0 && l.solo_time > 0 && " · "}{l.solo_time > 0 && `${l.solo_time}h solo`}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
